import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Col, Form, Modal, Row } from 'antd';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { SubmitButton } from '../../admin/components/Buttons';
import { InputField } from '../../admin/components/Inputs';
import styles from '../../admin/styles/Login.module.scss';
import { AdminUserContext, FirebaseContext } from '../../contexts';

async function isUserWorker(firebase, workerEmail) {
  const db = firebase.firestore();
  const workerSnapshot = await db
    .collection('users')
    .where('email', '==', workerEmail)
    .get();
  const workerData = workerSnapshot.docs[0]?.data();
  return Boolean(workerData);
}

const Login = () => {
  const router = useRouter();
  const { user, state, dispatch, verified, setVerified } = useContext(
    AdminUserContext,
  );
  const firebase = useContext(FirebaseContext);

  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [formError, setFormError] = useState();
  const [verificationCode, setVerificationCode] = useState();
  const [isShowingVerifyComponent, setIsShowingVerifyCOmponent] = useState(
    false,
  );

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [logoutMessage, setLogoutMessage] = useState('');

  // this need to be put in otp screen
  useEffect(() => {
    if (verified && user?.role === 'apostoAdmin') {
      router.push('/admin/management');
    } else if (verified && user) {
      router.push('/admin');
    } else if (!verified && user) {
      setIsShowingVerifyCOmponent(true);
      if (user && user.is2FAAuthenticated) {
        getVerificationCode(user.id);
        setIsShowingVerifyCOmponent(true);
      } else if (user && !user.is2FAAuthenticated) {
        setIsShowingVerifyCOmponent(false);
        router.push('/admin');
      }
    }
  }, [user, verified]);

  useEffect(() => {
    // Add a message informing user that this logout was due to inactivity
    const isInactivityLogout = Number(localStorage.getItem('inactivityLogout'));
    if (isInactivityLogout) {
      setLogoutMessage('You have been logged out due to inactivity.');
      localStorage.setItem('inactivityLogout', '0');
    }
  }, []);

  useEffect(() => {
    // If worker has logged in on another tab which causes the admin to log out.
    if (state.workerLoggedIn && state.adminLoggedIn) {
      setFormError(
        `You have been logged out of the system because you logged in to steward's portal`,
      );
      dispatch({ type: 'workerLoggedIn', value: false });
      dispatch({ type: 'adminLoggedIn', value: false });
    }
    // If we are currently logged in as worker
    else if (state.workerLoggedIn && !state.adminLoggedIn) {
      showModal();
    }
  }, [state?.workerLoggedIn]);

  function showModal() {
    Modal.confirm({
      title: 'You are already logged in as an steward.',
      icon: <ExclamationCircleOutlined />,
      content: `If you click on “Ok”, you will be redirected back to the steward portal. 
      However, if you intend to proceed to the event manager portal,
      kindly log out of the steward portal.`,
      okText: 'Ok',
      // hide cancel button
      cancelButtonProps: { style: { display: 'none' } },
      centered: true,
      width: '498px',
      onOk: () => router.push('/'),
    });
  }

  const getVerificationCode = async (id) => {
    const smsResponse = await fetch('/api/get-verification-code', {
      body: JSON.stringify({
        userId: id,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
  };

  const verifyCode = async (id) => {
    const verifyReponse = await fetch('/api/verify-code', {
      body: JSON.stringify({
        userId: id,
        code: verificationCode,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    return verifyReponse.json();
  };

  const onLogin = async () => {
    setFormError(null);
    setIsSubmitting(true);

    if (!email || !password) {
      setIsSubmitting(false);
      setFormError('Make sure to fill up your email and password');
      return;
    }

    if (await isUserWorker(firebase, email)) {
      setIsSubmitting(false);
      setFormError('Please login using Steward Portal');
      return;
    }

    try {
      await firebase
        .auth()
        .signInWithEmailAndPassword(email.trim().toLowerCase(), password);
    } catch (error) {
      setIsSubmitting(false);
      if (error.code === 'auth/network-request-failed') {
        setFormError(
          'Could not connect. Please check your internet connection.',
        );
      } else {
        setFormError('Invalid email or password. Please try again.');
      }
    }
  };

  const onVerification = async () => {
    setFormError(null);
    setIsSubmitting(true);
    if (verificationCode) {
      try {
        let res = await verifyCode(user.id);
        if (res?.result.status === 'approved') {
          setVerified(true);
          setIsSubmitting(false);
        } else {
          setIsSubmitting(false);
          setVerificationCode('');
          setFormError('Invalid code. Please try again');
        }
      } catch {
        setIsSubmitting(false);
        setVerificationCode('');
        setFormError('Invalid code. Please try again');
      }
    } else {
      setIsSubmitting(false);
      setFormError('Make sure to fill up your verification code');
    }
  };

  const handlePassword = () => {
    setShowPassword(!showPassword);
  };

  const renderLoginComponent = () => {
    return (
      <>
        <div>
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputField
              placeholder="Email"
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              value={email}
              suffix="icon-email"
              fadeSuffix
            />
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name="password"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputField
              placeholder="Password"
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              value={password}
              suffix="icon-unlock"
              fadeSuffix
              type={showPassword ? 'input' : 'password'}
              suffixClick={handlePassword}
            />
          </Form.Item>
        </div>
        {formError && <div className={styles.formError}>{formError}</div>}
        <SubmitButton
          loading={isSubmitting}
          title="Sign In"
          onChange={() => {}}
          onClick={onLogin}
        />
        <div
          onClick={() => router.push('/admin/forgot-password')}
          className={styles.forgotPassword}
        >
          I forgot my password
        </div>
        <div
          onClick={() => router.push('/app/login')}
          className={styles.adminLink}
        >
          Are you working at an event and looking for the Aposto App? Click
          here.
        </div>
      </>
    );
  };

  const renderVerificationComponent = () => {
    return (
      <>
        <div style={{ marginBottom: 20 }}>
          <InputField
            placeholder="Verification Code"
            onChange={(e) => {
              setVerificationCode(e.target.value);
            }}
            value={verificationCode}
            fadeSuffix
          />
        </div>
        {formError && <div className={styles.formError}>{formError}</div>}
        <SubmitButton
          title="Submit"
          onChange={() => {}}
          onClick={onVerification}
        />
      </>
    );
  };
  return (
    <div>
      <div>
        <div>
          <Row className={styles.loginContainer}>
            <Col span={12} className={styles.leftContainer}>
              <div>
                <img
                  src="/images/Logo_Event_Manager_Portal_full.png"
                  width={500}
                  height={157}
                />
              </div>
            </Col>
            <Col span={12} className={styles.inputContainer}>
              {logoutMessage && (
                <div className={styles.logoutMessage}>{logoutMessage}</div>
              )}
              {!isShowingVerifyComponent && renderLoginComponent()}
              {isShowingVerifyComponent && renderVerificationComponent()}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Login;
